import { defineStore } from "pinia";
import { ref } from "vue";
import ApiService from "../services/ApiService";

export const useAuthStore = defineStore("auth", () => {
  // 1. 상태 (State)
  const savedMindex =
    localStorage.getItem("mindex") || sessionStorage.getItem("mindex");
  const savedNickname =
    localStorage.getItem("mnickname") || sessionStorage.getItem("mnickname");

  // ⭐️ [중요] 저장된 토큰 불러오기
  const savedToken =
    localStorage.getItem("token") || sessionStorage.getItem("token");

  const mindex = ref(savedMindex || null);
  const mnickname = ref(savedNickname || null);

  // ⭐️ [중요] 여기가 빠지면 'token is not defined' 오류가 납니다.
  const token = ref(savedToken || null);

  const mlevel = ref(parseInt(localStorage.getItem("mlevel") || 1));
  const menergy = ref(parseInt(localStorage.getItem("menergy") || 50));


  // ⭐️ [추가] 로또 접근 권한 상태 (기본값 false)
  // 문자열 "true"로 저장될 수 있으므로 비교 연산 사용
  const savedAllowLotto = 
    localStorage.getItem("allow_lotto") === "true" || 
    sessionStorage.getItem("allow_lotto") === "true";
    
  const allowLotto = ref(savedAllowLotto);

  // 2. 게터 (Getters)
  const isLoggedIn = ref(!!mindex.value);

  // 3. 액션 (Actions)
  /**
   * 로그인 처리
   * @param {string} email
   * @param {string} password
   * @param {boolean} remember 자동 로그인 여부
   */
  async function login(email, password, remember = false) {
    try {
      const response = await ApiService.login(email, password);
      const user = response.data;

      // 상태 업데이트
      mindex.value = user.mindex;
      mnickname.value = user.mnickname;
      mlevel.value = user.mlevel;
      menergy.value = user.menergy;
      token.value = user.token; // ⭐️ 토큰 상태 업데이트

      // ⭐️ [추가] 권한 설정
      allowLotto.value = !!user.allow_lotto;

      isLoggedIn.value = true;

      // 스토리지 저장 분기 처리
      if (remember) {
        // 자동 로그인: localStorage (영구)
        localStorage.setItem("mindex", user.mindex);
        localStorage.setItem("mnickname", user.mnickname);
        localStorage.setItem("mlevel", user.mlevel);
        localStorage.setItem("menergy", user.menergy);
        localStorage.setItem("token", user.token); // ⭐️ 저장

        // ⭐️ [추가] 권한 영구 저장
        localStorage.setItem("allow_lotto", user.allow_lotto);

        // 세션 삭제
        sessionStorage.removeItem("mindex");
        sessionStorage.removeItem("mnickname");
        sessionStorage.removeItem("token");
      } else {
        // 일반 로그인: sessionStorage (브라우저 닫으면 삭제)
        sessionStorage.setItem("mindex", user.mindex);
        sessionStorage.setItem("mnickname", user.mnickname);
        sessionStorage.setItem("mlevel", user.mlevel);
        sessionStorage.setItem("menergy", user.menergy);
        sessionStorage.setItem("token", user.token); // ⭐️ 저장

        // ⭐️ [추가] 권한 세션 저장
        sessionStorage.setItem("allow_lotto", user.allow_lotto);

        // 로컬 삭제
        localStorage.removeItem("mindex");
        localStorage.removeItem("mnickname");
        localStorage.removeItem("token");
      }

      return true;
    } catch (error) {
      console.error("로그인 실패:", error);
      throw error;
    }
  }

  /**
   * 로그아웃 처리
   */
  function logout() {
    mindex.value = null;
    mnickname.value = null;
    token.value = null; // ⭐️ 초기화
    isLoggedIn.value = false;
    allowLotto.value = false; // ⭐️ [추가] 권한 초기화

    mlevel.value = 1;
    menergy.value = 50;
    localStorage.removeItem("mlevel");
    localStorage.removeItem("menergy");
    sessionStorage.removeItem("mlevel");
    sessionStorage.removeItem("menergy");

    // 모든 스토리지에서 제거
    localStorage.removeItem("mindex");
    localStorage.removeItem("mnickname");
    localStorage.removeItem("token"); // ⭐️ 삭제

    sessionStorage.removeItem("mindex");
    sessionStorage.removeItem("mnickname");
    sessionStorage.removeItem("token"); // ⭐️ 삭제

    localStorage.removeItem("allow_lotto"); // ⭐️ 삭제
    sessionStorage.removeItem("allow_lotto"); // ⭐️ 삭제
  }

  // 정보 갱신 액션 (퀴즈 후 사용)
  function updateUserInfo(level, energy) {
    mlevel.value = level;
    menergy.value = energy;
    // 현재 로그인된 스토리지 찾아서 업데이트
    const storage = localStorage.getItem("mindex")
      ? localStorage
      : sessionStorage;
    storage.setItem("mlevel", level);
    storage.setItem("menergy", energy);
  }

  async function checkAttendance() {
    if (!token.value) return; // 토큰 없으면 패스

    try {
      const response = await ApiService.checkAuth();
      const user = response.data;

      // 정보 갱신
      mindex.value = user.mindex;
      mnickname.value = user.mnickname;
      mlevel.value = user.mlevel;
      menergy.value = user.menergy;

      // ⭐️ [추가] 권한 정보 최신화
      allowLotto.value = !!user.allow_lotto;

      isLoggedIn.value = true;

      // 로컬 스토리지 등 최신화
      const storage = localStorage.getItem("mindex")
        ? localStorage
        : sessionStorage;
      storage.setItem("mlevel", user.mlevel);
      storage.setItem("menergy", user.menergy);

      // ⭐️ [추가] 권한 스토리지 업데이트
      storage.setItem("allow_lotto", user.allow_lotto);

      // ⭐️ 출석 보상을 받았다면 알림 표시 (선택 사항)
      if (user.attendance_reward) {
        alert(`📅 출석 체크 완료!\n에너지 50포인트가 지급되었습니다.`);
      }
    } catch (error) {
      console.error("토큰 만료 또는 오류:", error);
      logout(); // 토큰이 유효하지 않으면 로그아웃 처리
    }
  }

  return {
    mindex,
    mnickname,
    token, // ⭐️ 반환 객체에도 꼭 포함되어야 합니다.
    isLoggedIn,
    login,
    logout,
    mlevel,
    menergy,
    updateUserInfo,
    allowLotto, // ⭐️ [추가] 꼭 반환해야 다른 컴포넌트에서 씁니다!
    checkAttendance,
  };
});
